import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import Toast from '@/components/ui/Toast';
import QuoteRequestModal from './components/QuoteRequestModal';
import { products } from '@/mocks/products';
import { aed } from '@/lib/format';

const categories = [
  'All',
  'chairs',
  'softSeating',
  'workstations',
  'meetingTables',
  'components',
  'coffeeTables',
  'storage',
  'pedestals',
  'execDesks',
  'partitions',
  'reception',
];

export default function StorefrontPage() {
  const { t } = useTranslation();
  const [category, setCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const [quoteFor, setQuoteFor] = useState<{ name: string; sku: string } | null>(null);
  const [toast, setToast] = useState('');

  const live = products.filter((p) => p.published);
  const filtered = useMemo(() => live.filter((p) => category === 'All' || p.category === category), [category, live]);
  const visible = showAll ? filtered : filtered.slice(0, 6);
  const categoryLabel = (c: string) => (c === 'All' ? t('common.all') : t(`category.${c}`, c));

  return (
    <div>
      <PageHeader
        eyebrow={t('sf.eyebrow')}
        title={t('sf.title')}
        description={t('sf.description')}
        actions={
          <>
            <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-accent-200 bg-accent-50 px-2.5 text-[11.5px] font-medium text-accent-900">
              <i className="ri-eye-line text-[13px]" /> {t('sf.previewMode')}
            </span>
            <a
              href="https://treejartrading.ae"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-external-link-line text-[13px]" /> {t('sf.openLive')}
            </a>
            <button
              type="button"
              onClick={() => setToast(t('sf.publishToast', { count: live.length }))}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-upload-2-line text-[13px]" /> {t('sf.publishChanges')}
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="xl:col-span-3">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 bg-background-100 px-3 py-2">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-background-400" />
                <span className="h-2 w-2 rounded-full bg-background-400" />
                <span className="h-2 w-2 rounded-full bg-background-400" />
              </div>
              <div className="ml-2 flex h-6 flex-1 items-center gap-1.5 rounded border border-background-200 bg-background-50 px-2">
                <i className="ri-lock-2-line text-[10px] text-foreground-400" />
                <p className="text-[10.5px] text-foreground-600">{t('sf.urlPath')}</p>
              </div>
              <span className="rounded bg-primary-50 px-1.5 py-0.5 text-[9.5px] font-medium text-primary-800">{t('sf.preview')}</span>
            </div>

            <div className="bg-background-50">
              <div className="flex items-center justify-between gap-3 bg-primary-500 px-4 py-1.5 text-background-50 md:px-6">
                <p className="text-[10.5px]">{t('sf.topbar')}</p>
                <p className="hidden text-[10.5px] sm:block">+971 4 332 8800</p>
              </div>

              <div className="flex items-center justify-between gap-4 border-b border-background-200 px-4 py-3 md:px-6">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded bg-primary-500 text-background-50">
                    <i className="ri-plant-line text-[14px]" />
                  </span>
                  <div className="leading-none">
                    <p className="text-[13px] font-semibold tracking-tight text-foreground-950">TREEJAR</p>
                    <p className="mt-0.5 text-[8.5px] uppercase tracking-[0.18em] text-foreground-400">Trading LLC</p>
                  </div>
                </div>
                <nav className="hidden items-center gap-5 md:flex">
                  {[t('sf.nav1'), t('sf.nav2'), t('sf.nav3'), t('sf.nav4')].map((link, i) => (
                    <a
                      key={link}
                      href="https://treejartrading.ae"
                      target="_blank"
                      rel="noreferrer"
                      className={`text-[11.5px] transition-colors hover:text-primary-700 ${
                        i === 0 ? 'font-semibold text-foreground-950' : 'text-foreground-600'
                      }`}
                    >
                      {link}
                    </a>
                  ))}
                </nav>
                <button
                  type="button"
                  onClick={() => setQuoteFor({ name: t('sf.requestQuote'), sku: 'PROJECT' })}
                  className="h-7 cursor-pointer whitespace-nowrap rounded bg-foreground-950 px-3 text-[11px] font-semibold text-background-50 transition-opacity hover:opacity-90"
                >
                  {t('sf.requestQuote')}
                </button>
              </div>

              <div className="px-4 pb-5 pt-6 md:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary-700">{t('sf.categoryEyebrow')}</p>
                    <h2 className="mt-1 text-xl font-semibold text-foreground-950 md:text-2xl">{t('sf.heroTitle')}</h2>
                    <p className="mt-1 max-w-lg text-[12px] leading-5 text-foreground-500">{t('sf.heroSub')}</p>
                  </div>
                  <p className="text-[11px] text-foreground-400">{t('sf.products', { count: filtered.length })}</p>
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategory(c)}
                      className={`cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                        category === c
                          ? 'border-primary-500 bg-primary-500 text-background-50'
                          : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
                      }`}
                    >
                      {categoryLabel(c)}
                    </button>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {visible.map((p) => (
                    <article
                      key={p.sku}
                      className="group flex flex-col overflow-hidden rounded-md border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                    >
                      <div className="h-[150px] w-full overflow-hidden bg-background-100">
                        <img
                          src={p.image}
                          alt={`${p.name} — ${p.category} by Treejar`}
                          title={`${p.name} office furniture Dubai`}
                          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-3">
                        <div className="flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${p.stock > 0 ? 'bg-primary-500' : 'bg-accent-500'}`} />
                          <p className="text-[10px] font-medium text-foreground-600">
                            {p.stock > 0 ? t('sf.inStockDubai') : t('sf.availableIn', { weeks: 2 })}
                          </p>
                        </div>
                        <h3 className="mt-1 line-clamp-2 min-h-[32px] text-[12.5px] font-medium leading-4 text-foreground-950">{p.name}</h3>
                        <p className="mt-1 line-clamp-1 text-[10.5px] text-foreground-400">
                          {p.specs[0]?.value} · {p.color}
                        </p>
                        <div className="mt-2.5 flex items-end justify-between border-t border-background-200 pt-2.5">
                          <div>
                            <p className="text-[9px] uppercase tracking-[0.08em] text-foreground-400">{t('sf.from')}</p>
                            <p className="tnum text-[14px] font-semibold text-foreground-950">{aed(p.sale)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setQuoteFor({ name: p.name, sku: p.sku })}
                            className="h-7 cursor-pointer whitespace-nowrap rounded border border-primary-300 px-2 text-[10.5px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                          >
                            {t('sf.requestQuoteBtn')}
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {filtered.length > 6 && !showAll && (
                  <div className="mt-3 text-center">
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      className="cursor-pointer rounded border border-background-300 px-3 py-1 text-[11px] font-medium text-foreground-700 hover:bg-background-100"
                    >
                      {t('sf.showAll', { count: filtered.length })}
                    </button>
                  </div>
                )}

                {visible.length === 0 && (
                  <p className="rounded border border-dashed border-background-300 px-4 py-8 text-center text-[12px] text-foreground-500">
                    {t('sf.noProducts')}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 border-t border-background-200 bg-background-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-6">
                <p className="text-[10.5px] text-foreground-500">{t('sf.address')}</p>
                <p className="text-[10px] text-foreground-400">{t('sf.footerNote')}</p>
              </div>
            </div>
          </div>

          <p className="mt-2 px-1 text-[10.5px] text-foreground-400">{t('sf.previewNote')}</p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('sf.rulesTitle')}</h2>
            <ul className="mt-2 space-y-2 text-[11.5px] leading-4 text-foreground-600">
              <li className="flex gap-1.5">
                <i className="ri-check-line text-[12px] text-primary-600" /> {t('sf.rule1')}
              </li>
              <li className="flex gap-1.5">
                <i className="ri-check-line text-[12px] text-primary-600" /> {t('sf.rule2')}
              </li>
              <li className="flex gap-1.5">
                <i className="ri-close-line text-[12px] text-rose-500" /> {t('sf.rule3')}
              </li>
              <li className="flex gap-1.5">
                <i className="ri-close-line text-[12px] text-rose-500" /> {t('sf.rule4')}
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-950">{t('sf.pubTitle')}</h2>
            <div className="mt-2 space-y-2">
              {[
                [t('sf.publishedItems'), String(live.length), 'primary'],
                [t('sf.hiddenItems'), String(products.length - live.length), 'neutral'],
                [t('sf.blockedByContract'), '3', 'accent'],
              ].map(([label, value, tone]) => (
                <div key={label} className="flex items-center justify-between text-[11.5px]">
                  <span className="text-foreground-500">{label}</span>
                  <span
                    className={`tnum rounded px-1.5 py-0.5 font-medium ${
                      tone === 'primary'
                        ? 'bg-primary-50 text-primary-800'
                        : tone === 'accent'
                          ? 'bg-accent-50 text-accent-900'
                          : 'bg-background-100 text-foreground-600'
                    }`}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-2 border-t border-background-200 pt-2 text-[10.5px] leading-4 text-foreground-400">
              {t('sf.blockedNote')}
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
            <div className="flex items-center gap-1.5">
              <i className="ri-information-line text-[13px] text-foreground-400" />
              <p className="text-[11px] font-semibold text-foreground-700">{t('sf.clientActions')}</p>
            </div>
            <p className="mt-1 text-[11px] leading-4 text-foreground-500">{t('sf.clientActionsBody')}</p>
          </div>
        </div>
      </div>

      {quoteFor && <QuoteRequestModal productName={quoteFor.name} sku={quoteFor.sku} onClose={() => setQuoteFor(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast('')} />}
    </div>
  );
}